import { useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { InsuranceType } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/shared/enums.pb';
import {
  Plan,
  OnederfulPayerIDResponse,
} from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v1/insurance_verification_adapter.pb';
import {
  ErrorDetails,
  DentalCoverageResponse,
  DentalCoverageRequest,
  InsuranceCoverageInfoRequest,
  InsuranceCoverageResponse,
} from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v2/insurance_verification_adapter.pb';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { InsuranceQueries } from '@frontend/api-insurance';
import { InsuranceDetailQueries } from '@frontend/api-insurance-detail';
import { useTranslation } from '@frontend/i18n';
import { useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails } from '@frontend/insurance-verification';
import { useMutation } from '@frontend/react-query-helpers';
import {
  SchemaInsuranceVerificationAdapterServiceV1,
  SchemaInsuranceVerificationAdapterServiceV2,
} from '@frontend/schema';
import { titleCase } from '@frontend/string';
import { theme } from '@frontend/theme';
import {
  Combobox,
  ContentLoader,
  DatePickerField,
  DropdownField,
  DropdownSearchField,
  FormRow,
  Heading,
  TextField,
  Tray,
  useAlert,
} from '@frontend/design-system';
import { useGetLocationVerticalForPayerList } from '../../hooks/use-get-location-vertical-for-payer-list';
import { useGetVerifiedInsuranceDetailsShallowStore } from '../../hooks/use-get-verified-insurance-details-store';
import { useInsuranceForm } from '../../hooks/use-insurance-form';
import { useInsuranceType } from '../../providers/insurance-type-provider';
import { usePersonInfoStore } from '../../providers/person-info-provider';
import { verifyInsuranceFormTrackingIds } from '../../tracking-ids';
import {
  MONTH_INFO,
  OTHER_SUBSCRIBER_SELECTION_VALUE,
  RELATIONSHIP_LIST,
  RelationshipEnum,
  VerificationError,
} from '../../utils/constants';
import { InsuranceDetails } from '../../utils/types';
import {
  getPayerIdAndPayerNameFromCombinedId,
  getRelationShipValue,
  removeNonAlphaNumericCharacters,
} from '../../utils/utils';
import { ErrorBanner } from '../error-banner';
import { PlanSearchInput } from './plan-search-input';
import { VerifyInsuranceSuccessSection } from './verify-insurance-success-section';

interface VerifyInsuranceSidePanelBodyProps {
  hasVerificationError: boolean;
  shouldShowTuuthFairyReport: boolean;
  onClose: () => void;
  onReportLearnMoreClick: () => void;
}

dayjs.extend(utc);

export const VerifyInsuranceForm = ({
  hasVerificationError,
  shouldShowTuuthFairyReport,
  onClose,
  onReportLearnMoreClick,
}: VerifyInsuranceSidePanelBodyProps) => {
  const { t } = useTranslation('insuranceDetails');
  const alert = useAlert();
  const { personInfo, locationId } = usePersonInfoStore();
  const personId = personInfo?.PersonID ?? '';

  const isVerifyButtonClicked = useRef(false);
  const [errorDetails, setErrorDetails] = useState<ErrorDetails>();
  const [showSuccessPage, setShowSuccessPage] = useState(false);
  const [successPageInsuranceDetails, setSuccessPageInsuranceDetails] = useState<Partial<InsuranceDetails>>();

  const { refetchInsuranceDetails } = useGetVerifiedInsuranceDetailsShallowStore('refetchInsuranceDetails');

  const { isDental } = useGetLocationVerticalForPayerList(locationId || '');
  const { insuranceType } = useInsuranceType();

  const {
    isLoading,
    form,
    payerList,
    nationalProviderIdList,
    primSubscriberList,
    practiceTypeListOptions,
    isWellnessVertical,
    insuranceErrorDetails,
  } = useInsuranceForm();

  const {
    invalidateGetProviderListQuery,
    invalidateGetInsuranceVerificationMetrics,
    invalidateGetInsuranceByPersonIdQuery,
    invalidateGetInsuranceVerificationDetailsPersonIdQuery,
    invalidateGetNonDentalInsuranceVerificationDetailsByPersonIdQuery,
    invalidateGetDentalInsuranceDetailsByPerson,
  } = InsuranceDetailQueries.useInvalidateQueries();

  const { invalidateGetPracticeTypeList, invalidateCheckInsuranceCountQuery } = InsuranceQueries.useInvalidateQueries();

  const alphabeticallySortedPayerList = payerList.sort((a, b) => a.label.localeCompare(b.label));

  const { locationIdWiseCustomizationFlagDetails } = useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails();
  const isEnhancedInsuranceVerificationEnabled = locationIdWiseCustomizationFlagDetails?.[locationId!] || false;
  const isMetlifePayerSelected = form?.values?.payerId?.toLowerCase().includes('metlife');

  const handleDentalEnhancedInsuranceVerificationFlow = (res: DentalCoverageResponse) => {
    if (res) {
      onClose();
      alert.success(t('Insurance Details Submitted for Verification!'));
    } else {
      alert.error(t('Something went wrong. Please try again.'));
    }
  };

  const refetchInsuranceVerificationDetails = (personId: string, locationId: string, insuranceType: InsuranceType) => {
    if (refetchInsuranceDetails) {
      refetchInsuranceDetails?.();
    } else {
      invalidateGetInsuranceVerificationDetailsPersonIdQuery(personId, locationId, insuranceType);
    }
  };

  const handleDentalInsuranceVerificationFlow = (res: DentalCoverageResponse, payerId: string, payerName: string) => {
    if (res.coverage?.status) {
      const primarySubscriberValue = form.values.primarySubscriber;
      const subscriberId =
        !!primarySubscriberValue && primarySubscriberValue !== OTHER_SUBSCRIBER_SELECTION_VALUE
          ? primarySubscriberValue
          : '';

      setSuccessPageInsuranceDetails({
        payerId: payerId,
        payerName: payerName,
        groupName: form.values.groupName,
        groupNumber: form.values.groupNumber,
        firstName: form.values.patientFirstName,
        lastName: form.values.patientLastName,
        dateOfBirth: form.values.patientBirthDate,
        memberId: form.values.patientMemberId,
        nationalProviderId: form.values.nationalProviderId,
        providerTaxId: form.values.taxId,
        relationship: form.values.relationship as RelationshipEnum,
        subscriberId,
        subscriberFirstName: form.values.subscriberFirstName,
        subscriberLastName: form.values.subscriberLastName,
        subscriberDateOfBirth: form.values.subscriberBirthDate,
        subscriberMemberId: form.values.subscriberMemberId,
        coverageStatus: titleCase(res?.coverage?.status || ''),
        effectiveDate: res?.coverage?.effectiveDate || '',
      });
      setShowSuccessPage(true);
      alert.success(t('Insurance Verified!'));
    } else {
      // handle error case
      setErrorDetails(res.errorDetails);
    }
  };

  const { mutate: updateInsuranceDetails, isLoading: isUpdating } = useMutation({
    mutationFn: (data: DentalCoverageRequest) =>
      SchemaInsuranceVerificationAdapterServiceV2.GetDentalCoverageInfo(
        data,
        InsuranceDetailQueries.getLocationIdHeader(locationId)
      ),
    onSuccess: (res: DentalCoverageResponse) => {
      const { payerId, payerName } = getPayerIdAndPayerNameFromCombinedId(form.values.payerId || '');
      if (!isEnhancedInsuranceVerificationEnabled) {
        handleDentalInsuranceVerificationFlow(res, payerId, payerName);
      } else {
        handleDentalEnhancedInsuranceVerificationFlow(res);
      }
      const isNpiInProviderList =
        form.values.nationalProviderId && nationalProviderIdList.includes(form.values.nationalProviderId);

      if (isNpiInProviderList) {
        invalidateGetProviderListQuery(locationId!);
      }
      invalidateGetInsuranceVerificationMetrics(MONTH_INFO.startDate, MONTH_INFO.endDate, locationId!);
      invalidateGetInsuranceByPersonIdQuery(personId, locationId!);
      refetchInsuranceVerificationDetails(personId, locationId!, insuranceType);
      invalidateGetNonDentalInsuranceVerificationDetailsByPersonIdQuery(personId, locationId!);
      invalidateGetDentalInsuranceDetailsByPerson(personId);
      invalidateCheckInsuranceCountQuery();
    },
    onError: () => {
      alert.error(t('Something went wrong while verifying insurance. Please try again.'));
    },
  });

  const { mutate: insuranceCoverageInfo, isLoading: isLoadingInsuranceCoverageInfo } = useMutation({
    mutationFn: (data: InsuranceCoverageInfoRequest) =>
      SchemaInsuranceVerificationAdapterServiceV2.GetInsuranceCoverageInfo(
        data,
        InsuranceDetailQueries.getLocationIdHeader(locationId)
      ),
    onSuccess: (res: InsuranceCoverageResponse) => {
      if (res.coverage?.status) {
        const { payerId, payerName } = getPayerIdAndPayerNameFromCombinedId(form.values.payerId || '');
        const primarySubscriberValue = form.values.primarySubscriber;
        const subscriberId =
          !!primarySubscriberValue && primarySubscriberValue !== OTHER_SUBSCRIBER_SELECTION_VALUE
            ? primarySubscriberValue
            : '';

        setSuccessPageInsuranceDetails({
          payerId: payerId,
          payerName: payerName,
          groupName: form.values.groupName,
          groupNumber: form.values.groupNumber,
          firstName: form.values.patientFirstName,
          lastName: form.values.patientLastName,
          dateOfBirth: form.values.patientBirthDate,
          memberId: form.values.patientMemberId,
          nationalProviderId: form.values.nationalProviderId,
          providerTaxId: form.values.taxId,
          relationship: form.values.relationship as RelationshipEnum,
          subscriberId,
          subscriberFirstName: form.values.subscriberFirstName,
          subscriberLastName: form.values.subscriberLastName,
          subscriberDateOfBirth: form.values.subscriberBirthDate,
          subscriberMemberId: form.values.subscriberMemberId,
          effectiveDate: res?.coverage?.effectiveDate,
          requestId: res?.externalRequestId || '',
          coverageStatus: titleCase(res?.coverage?.status || ''),
        });
        setShowSuccessPage(true);
        alert.success(t('Insurance Verified!'));
      } else {
        // handle error case
        setErrorDetails(res.errorDetails);
      }
      const isNpiInProviderList =
        form.values.nationalProviderId && nationalProviderIdList.includes(form.values.nationalProviderId);

      if (isNpiInProviderList) {
        invalidateGetProviderListQuery(locationId!);
      }
      invalidateGetInsuranceVerificationMetrics(MONTH_INFO.startDate, MONTH_INFO.endDate, locationId!);
      invalidateGetInsuranceByPersonIdQuery(personId, locationId!);
      refetchInsuranceVerificationDetails(personId, locationId!, insuranceType);
      invalidateGetNonDentalInsuranceVerificationDetailsByPersonIdQuery(personId, locationId!);
      invalidateGetPracticeTypeList(locationId!);
      invalidateCheckInsuranceCountQuery();
    },
    onError: () => {
      alert.error(t('Something went wrong while verifying insurance. Please try again.'));
    },
  });

  const handleSelectPlan = (plan: Plan) => {
    if (plan.cNam || plan.paID) {
      SchemaInsuranceVerificationAdapterServiceV1.GetOnederfulPayerID({
        electronicPayerId: plan.paID,
        payerName: plan.cNam,
      }).then((res: OnederfulPayerIDResponse) => {
        const isOnederfulPayerIdPresent =
          !!res.onederfulPayerId && res.onederfulPayerId !== VerificationError.WEAVE_PAYER_NOT_SUPPORTED;
        if (isOnederfulPayerIdPresent) {
          form.seedValues({
            payerId: res.onederfulPayerId ?? '',
            groupNumber: removeNonAlphaNumericCharacters(plan.gNum) ?? '',
            groupName: plan.gNam ?? '',
          });
        } else {
          form.getFieldProps('payerId').error = t('Payer Not supported');
          form.seedValues({
            groupNumber: removeNonAlphaNumericCharacters(plan.gNum) ?? '',
            groupName: plan.gNam ?? '',
          });
          alert.error(t('Payer Not Supported for selected plan!'));
        }
      });
    } else {
      form.seedValues({
        groupNumber: removeNonAlphaNumericCharacters(plan.gNum) ?? '',
        groupName: plan.gNam ?? '',
      });
    }
  };

  const handleDentalInsuranceCoverageInfo = (payerId: string, payerName: string, isDependent: boolean) => {
    updateInsuranceDetails({
      personId,
      payer: {
        id: payerId,
        name: payerName,
      },
      provider: {
        nationalProviderId: form.values.nationalProviderId,
        taxId: form.values.taxId,
      },
      relationship: getRelationShipValue(form.values.relationship as RelationshipEnum),
      patient: {
        isDependent,
        firstName: form.values.patientFirstName,
        lastName: form.values.patientLastName,
        dateOfBirth: form.values.patientBirthDate,
        memberId: form.values.patientMemberId,
        groupNumber: form.values.groupNumber,
        zipCode: form.values.patientZipCode,
      },
      ...(isDependent && {
        subscriber: {
          firstName: form.values.subscriberFirstName,
          lastName: form.values.subscriberLastName,
          dateOfBirth: form.values.subscriberBirthDate,
          memberId: form.values.subscriberMemberId,
          zipCode: form.values.subscriberZipCode,
        },
      }),
      insuranceType,
    });
  };

  const handleNonDentalInsuranceCoverageInfo = (payerId: string, payerName: string, isDependent: boolean) => {
    insuranceCoverageInfo({
      personId,
      payer: {
        id: payerId,
        name: payerName,
      },
      provider: {
        nationalProviderId: form.values.nationalProviderId,
        taxId: form.values.taxId,
      },
      relationship: getRelationShipValue(form.values.relationship as RelationshipEnum),
      patient: {
        dateOfBirth: form.values.patientBirthDate,
        firstName: form.values.patientFirstName,
        lastName: form.values.patientLastName,
        groupName: form.values.groupName,
        groupNumber: form.values.groupNumber,
        memberId: form.values.patientMemberId,
        isDependent: form.values.relationship !== RelationshipEnum.Self,
      },
      practiceType: form.values.practiceType,
      dateOfService: dayjs().utc().format('MM/DD/YYYY'), //TODO: The date of service will be the appointment date currently sending the current date
      ...(isDependent && {
        subscriber: {
          firstName: form.values.subscriberFirstName,
          lastName: form.values.subscriberLastName,
          dateOfBirth: form.values.subscriberBirthDate,
          memberId: form.values.subscriberMemberId,
        },
      }),
    });
  };

  const handleSaveClick = () => {
    form.validate();
    isVerifyButtonClicked.current = true;
    if (form.isComplete) {
      const isDependent = form.values.relationship !== RelationshipEnum.Self;
      const { payerId, payerName } = getPayerIdAndPayerNameFromCombinedId(form.values.payerId || '');

      if (!isDental) {
        handleNonDentalInsuranceCoverageInfo(payerId, payerName, isDependent);
      } else {
        handleDentalInsuranceCoverageInfo(payerId, payerName, isDependent);
      }
    }
  };

  const errorDisplayOverride = isVerifyButtonClicked.current || hasVerificationError ? {} : { error: '' };

  const shouldRenderSubscriberInfo = useMemo(() => {
    const isRelationshipSelf =
      !!form.values.relationship &&
      (form.values.relationship === RelationshipEnum.Self || form.values.relationship?.toLowerCase() === 'self');
    const hasMultipleSubscribers = primSubscriberList.length > 1;
    const hasNoSubscribers = primSubscriberList.length === 0;

    // if the relationship is not self and we have multiple subscribers, we should render subscriber info only if the primary subscriber is selected
    if (hasMultipleSubscribers && !isRelationshipSelf) {
      return !!form.values.primarySubscriber;
    }

    // if the relationship is not self and we have no subscribers, we should render subscriber info to allow the user to add subscriber info manually without selecting the primary subscriber
    if (form?.values?.relationship && !isRelationshipSelf && hasNoSubscribers) {
      return true;
    }

    return false;
  }, [form.values.relationship, primSubscriberList.length, form.values.primarySubscriber]);

  if (showSuccessPage && successPageInsuranceDetails) {
    return (
      <VerifyInsuranceSuccessSection
        insuranceDetails={successPageInsuranceDetails}
        shouldShowTuuthFairyReport={shouldShowTuuthFairyReport}
        onClose={onClose}
        onReportLearnMoreClick={onReportLearnMoreClick}
      />
    );
  }

  return (
    <>
      <ContentLoader show={isLoading || isUpdating || isLoadingInsuranceCoverageInfo} />
      <ErrorBanner errorDetails={errorDetails || insuranceErrorDetails} customCss={{ marginTop: 0 }} />
      {isDental && (
        <PlanSearchInput
          locationId={locationId ?? ''}
          sourceId={personInfo?.SourceID ?? ''}
          onPlanSelect={handleSelectPlan}
          data-trackingid={verifyInsuranceFormTrackingIds.planSearchInput}
        />
      )}
      <form {...form.formProps} autoComplete='off'>
        <Heading level={3} css={{ marginBottom: theme.spacing(3) }}>
          {t('Patient Information')}
        </Heading>
        <FormRow>
          <DropdownSearchField
            {...form.getFieldProps('payerId')}
            placeholder={t('Select Insurance Payer*')}
            label={t('Select Insurance Payer*')}
            searchFieldPlaceholder={t('Search Payer')}
            data-trackingid={verifyInsuranceFormTrackingIds.payerNameDropdown}
            {...errorDisplayOverride}
          >
            {alphabeticallySortedPayerList.map((payer) => (
              <DropdownSearchField.Option
                searchValue={payer.label}
                key={payer.value + payer.label}
                value={payer.uniqueCombinedId ?? payer.value}
              >
                {payer.label}
              </DropdownSearchField.Option>
            ))}
          </DropdownSearchField>
        </FormRow>

        <FormRow cols={[50, 50]}>
          <TextField
            data-trackingid={verifyInsuranceFormTrackingIds.groupNameInput}
            {...form.getFieldProps('groupName')}
            label={t('Group Name')}
          />
          <TextField
            data-trackingid={verifyInsuranceFormTrackingIds.groupNumberInput}
            {...form.getFieldProps('groupNumber')}
            label={t('Group Number')}
          />
        </FormRow>

        <FormRow>
          <TextField
            data-trackingid={verifyInsuranceFormTrackingIds.memberIdInput}
            {...form.getFieldProps('patientMemberId')}
            label={t('Select Member ID*')}
            {...errorDisplayOverride}
          />
        </FormRow>
        <FormRow>
          <TextField
            data-trackingid={verifyInsuranceFormTrackingIds.firstNameInput}
            {...form.getFieldProps('patientFirstName')}
            label={t('Enter First Name*')}
            {...errorDisplayOverride}
          />
        </FormRow>
        <FormRow>
          <TextField
            data-trackingid={verifyInsuranceFormTrackingIds.lastNameInput}
            {...form.getFieldProps('patientLastName')}
            label={t('Enter Last Name*')}
            {...errorDisplayOverride}
          />
        </FormRow>
        <FormRow>
          <DatePickerField
            data-trackingid={verifyInsuranceFormTrackingIds.dobInput}
            {...form.getFieldProps('patientBirthDate')}
            label={t('Date of Birth*')}
            css={datePickerStyle}
            {...errorDisplayOverride}
          />
        </FormRow>
        {isEnhancedInsuranceVerificationEnabled && isMetlifePayerSelected && (
          <FormRow>
            <TextField
              data-trackingid={verifyInsuranceFormTrackingIds.zipCodeInput}
              {...form.getFieldProps('patientZipCode')}
              label={t('Patient Zip Code')}
              {...errorDisplayOverride}
            />
          </FormRow>
        )}
        {!isDental && isWellnessVertical && (
          <FormRow>
            <DropdownSearchField
              {...form.getFieldProps('practiceType')}
              placeholder={t('Select Practice Type')}
              label={t('Practice Type*')}
              searchFieldPlaceholder={t('Search Payer')}
              {...errorDisplayOverride}
            >
              {practiceTypeListOptions?.map((practiceType) => (
                <DropdownSearchField.Option
                  searchValue={practiceType.id}
                  key={practiceType.id + practiceType.label}
                  value={practiceType.id}
                >
                  {practiceType.label}
                </DropdownSearchField.Option>
              ))}
            </DropdownSearchField>
          </FormRow>
        )}
        <Heading level={3} css={{ marginBottom: theme.spacing(3) }}>
          {t('Provider Information')}
        </Heading>
        <FormRow cols={[50, 50]}>
          <Combobox
            data-trackingid={verifyInsuranceFormTrackingIds.nationalProviderIdInput}
            options={nationalProviderIdList}
            name='nationalProviderId'
            label={t('NPI Selector*')}
            fieldProps={{ ...form.getFieldProps('nationalProviderId'), ...errorDisplayOverride }}
          />
          <TextField
            data-trackingid={verifyInsuranceFormTrackingIds.taxIdInput}
            {...form.getFieldProps('taxId')}
            label={t('Tax ID*')}
            {...errorDisplayOverride}
          />
        </FormRow>
        <Heading level={3} css={{ marginBottom: theme.spacing(3) }}>
          {t('Relationship to Subscriber')}
        </Heading>
        <FormRow cols={[50, 50]}>
          <DropdownField
            data-trackingid={verifyInsuranceFormTrackingIds.relationshipToSubscriberDropdown}
            {...form.getFieldProps('relationship')}
            label={t('Relationship*')}
            placeholder={t('Select Relationship')}
            {...errorDisplayOverride}
          >
            {RELATIONSHIP_LIST.map(({ label, value }) => (
              <DropdownField.Option value={value} key={value}>
                {label}
              </DropdownField.Option>
            ))}
          </DropdownField>
          {!!primSubscriberList.length &&
            primSubscriberList.length > 1 &&
            form.values.relationship &&
            form.values.relationship !== RelationshipEnum.Self && (
              <div style={{ minWidth: 0 }}>
                <DropdownField
                  data-trackingid={verifyInsuranceFormTrackingIds.primarySubscriberDropdown}
                  {...form.getFieldProps('primarySubscriber')}
                  label={t('Subscriber')}
                  dropdownListStyle={css({ minWidth: 0 })}
                >
                  {primSubscriberList.map(({ label, value }) => (
                    <DropdownField.Option key={value} value={value}>
                      {label}
                    </DropdownField.Option>
                  ))}
                </DropdownField>
              </div>
            )}
        </FormRow>
        {shouldRenderSubscriberInfo && (
          <>
            <Heading level={3} css={{ marginBottom: theme.spacing(3) }}>
              {t('Subscriber Information')}
            </Heading>
            <FormRow>
              <TextField
                data-trackingid={verifyInsuranceFormTrackingIds.subscriberLastNameInput}
                {...form.getFieldProps('subscriberFirstName')}
                label={t('Subscriber First Name*')}
                {...errorDisplayOverride}
              />
            </FormRow>
            <FormRow>
              <TextField
                data-trackingid={verifyInsuranceFormTrackingIds.subscriberLastNameInput}
                {...form.getFieldProps('subscriberLastName')}
                label={t('Subscriber Last Name*')}
                {...errorDisplayOverride}
              />
            </FormRow>
            <FormRow cols={[50, 50]}>
              <DatePickerField
                data-trackingid={verifyInsuranceFormTrackingIds.subscriberDobInput}
                {...form.getFieldProps('subscriberBirthDate')}
                label={t('Date of Birth*')}
                {...errorDisplayOverride}
                css={datePickerStyle}
              />
              <TextField
                data-trackingid={verifyInsuranceFormTrackingIds.subscriberMemberIdInput}
                {...form.getFieldProps('subscriberMemberId')}
                label={t('Member ID*')}
                {...errorDisplayOverride}
              />
            </FormRow>
            {isEnhancedInsuranceVerificationEnabled && isMetlifePayerSelected && (
              <FormRow>
                <TextField
                  data-trackingid={verifyInsuranceFormTrackingIds.subscriberZipCodeInput}
                  {...form.getFieldProps('subscriberZipCode')}
                  label={t('Subscriber Zip Code')}
                  {...errorDisplayOverride}
                />
              </FormRow>
            )}
          </>
        )}
      </form>
      <Tray.Actions
        disabledPrimary={isLoading || isUpdating}
        onSecondaryClick={onClose}
        primaryLabel={t('Verify')}
        onPrimaryClick={handleSaveClick}
        primaryTrackingId={verifyInsuranceFormTrackingIds.verifyButton}
        secondaryTrackingId={verifyInsuranceFormTrackingIds.cancelButton}
      />
    </>
  );
};

const datePickerStyle = css({
  maxWidth: '100%',
});
